import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {message} from 'antd';
import TablePaginationBar from '../../../components/TablePaginationBar';

import {useHashState} from 'utils/hashState';
import { getVoucherClearingFilterInitialValue, validateVoucherClearingFilters } from './config/filterOptions';
import {VoucherClearingColumns} from "./components/VoucherClearingColumns";
import {useVoucherClearingQuery} from "./hooks/useVoucherClearingQuery";
import VoucherClearingFilter from "./components/VoucherClearingFilter";
import { VoucherClearingFilterType, VoucherClearingType } from './graphql/types/VoucherClearingType';

const VoucherClearingPage: React.FC = () => {
    const {t} = useTranslation();
    const [tempUserType, setTempUserType] = useState(
        () => getVoucherClearingFilterInitialValue().userType
    );
    const [activeUserType, setActiveUserType] = useState(
        () => getVoucherClearingFilterInitialValue().userType
    );
    const initialHashFilters = useMemo(() => {
        const { userType: _, ...rest } = getVoucherClearingFilterInitialValue();
        return rest;
    }, []);
    const [tempHashFilters, setTempHashFilters] = useHashState(initialHashFilters);
    const [hashFilters, setHashFilters] = useHashState(initialHashFilters);
    const tempFilters: VoucherClearingFilterType = useMemo(
        () => ({ ...tempHashFilters, userType: tempUserType }),
        [tempHashFilters, tempUserType]
    );
    const filters: VoucherClearingFilterType = useMemo(
        () => ({ ...hashFilters, userType: activeUserType }),
        [hashFilters, activeUserType]
    );
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<VoucherClearingType[]>([]);
    // 页面进入时不加载数据，直到点击 Filter 并通过校验
    const [shouldFetch, setShouldFetch] = useState(false);
    
    // 使用自定义hook获取数据
    const {
        voucherClearingData,
        loading,
        totalElements,
    } = useVoucherClearingQuery(filters, currentPage, pageSize, shouldFetch);

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: VoucherClearingType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const handleResetFilters = () => {
        const initial = getVoucherClearingFilterInitialValue();
        setTempUserType(initial.userType);
        setActiveUserType(initial.userType);
        setTempHashFilters({ ...initialHashFilters, userType: undefined });
        setHashFilters({ ...initialHashFilters, userType: undefined });
        setCurrentPage(1);
        setShouldFetch(false);
    };

    const setTempFilters = (newFilters: VoucherClearingFilterType) => {
        const { userType: newUserType, ...rest } = newFilters;
        setTempUserType(newUserType);
        setTempHashFilters({ ...rest, userType: undefined });
    };

    const handleFilter = () => {
        const validationError = validateVoucherClearingFilters(tempFilters);
        if (validationError) {
            message.warning(validationError);
            return;
        }

        setActiveUserType(tempUserType);
        setHashFilters({ ...tempHashFilters, userType: undefined });
        setCurrentPage(1);
        setShouldFetch(true);
    };

    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
                {/* 筛选条件 */}
                <VoucherClearingFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    handleFilter={handleFilter}
                    handleResetFilters={handleResetFilters}
                />

                {/* 订单列表 */}
                <VoucherClearingColumns
                    rowData={voucherClearingData}
                    loading={loading}
                />

                {/* 分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                />
        </div>
    );
};

export default VoucherClearingPage;